import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productService } from '../../services/api';
import { getProductImageUrl } from '../../utils/imageHelper';
import styles from './TopRated.module.css';

const TopRated = () => {
  const [topProducts, setTopProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadTopRatedProducts();
  }, []);

  const loadTopRatedProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await productService.getTopRatedProducts();

      if (response.success && response.data?.products) {
        setTopProducts(response.data.products);
      } else {
        setTopProducts([]);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des produits les mieux notés:', err);
      setError('Erreur lors du chargement des produits');
      setTopProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Gérer l'image du produit
  const getProductImage = (product) => {
    if (product.primary_image) {
      return getProductImageUrl(product.primary_image);
    }
    if (product.images?.[0]) {
      return getProductImageUrl(product.images[0]);
    }
    return '/images/leaves-right.png';
  };

  // Gérer le prix
  const getPrice = (product) => {
    const currentPrice = parseFloat(product.sale_price || product.price || 0);
    const oldPrice = product.sale_price ? parseFloat(product.price) : null;
    return { currentPrice, oldPrice };
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  if (isLoading) {
    return (
      <section className={styles.topRated}>
        <h2 className={styles.sectionTitle}>Produits les mieux notés</h2>
        <div className={styles.loading}>
          <p>Chargement des produits les mieux notés...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.topRated}>
        <h2 className={styles.sectionTitle}>Produits les mieux notés</h2>
        <div className={styles.error}>
          <p>{error}</p>
        </div>
      </section>
    );
  }

  if (topProducts.length === 0) {
    return (
      <section className={styles.topRated}>
        <h2 className={styles.sectionTitle}>Produits les mieux notés</h2>
        <div className={styles.empty}>
          <p>Aucun produit noté pour le moment.</p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.topRated}>
      <h2 className={styles.sectionTitle}>Produits les mieux notés</h2>
      <div className={styles.topRatedGrid}>
        {topProducts.map((product) => {
          const { currentPrice, oldPrice } = getPrice(product);
          const imageUrl = getProductImage(product);

          return (
            <div
              key={product.id}
              className={styles.topRatedProduct}
              onClick={() => handleProductClick(product.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className={styles.topRatedImage}>
                <img src={imageUrl} alt={product.name} />
                {product.rating > 0 && (
                  <div className={styles.ratingBadge}>
                    ⭐ {parseFloat(product.rating).toFixed(1)}
                  </div>
                )}
              </div>
              <div className={styles.topRatedInfo}>
                <div className={styles.topRatedTitle}>{product.name}</div>
                <div className={styles.topRatedPrice}>
                  {currentPrice.toLocaleString('fr-FR')} FCFA
                  {oldPrice && (
                    <span className={styles.oldPrice}>
                      {oldPrice.toLocaleString('fr-FR')} FCFA
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default TopRated;